import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { apiRequest } from '../../services/apiClient';
import inquiryTypeMasterService from '../../api/inquiryTypeMasterService';
import inquiryCategoryMasterService from '../../api/inquiryCategoryMasterService';
import inquirySubCategoryMasterService from '../../api/inquirySubCategoryMasterService';
import inquiryEscalationLevelMasterService from '../../api/inquiryEscalationLevelMasterService';
import { ticketService } from '../../api/ticketService';
import { formatTicketTagsLabel } from '../../utils/ticketTagDisplay';
import { getAssignableUsers, getUserDisplayName, isTicketHolderRole } from '../../api/userManagementService';
import { registrationService } from '../../api/registrationService';
import { useStatuses } from '../../hooks/useStatuses';
import { extractErrorMessage } from '../../utils/errorHandler';
import {
  masterRows,
  optionValue,
  optionalId,
  optionalBool,
  compactFilters,
  resolveTicketCategoryLabel,
  resolveTicketPriorityLabel,
  resolveCurrentSlaWindow,
} from '../../utils/inquiryDisplay';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { 
  FileText, 
  Calendar,
  Search,
  RefreshCw,
  AlertTriangle,
  Clock,
  User,
  Ticket as TicketIcon,
  ChevronLeft,
  ChevronRight,
  Download,
  ChevronDown,
  Filter,
  Eye,
  X
} from 'lucide-react';
import { formatDateTime } from '../../components/globaldeclaration';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

const getCurrentMonthDateRange = () => {
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const formatLocalDate = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  return {
    fromDate: formatLocalDate(firstOfMonth),
    toDate: formatLocalDate(today)
  };
};

const getRequesterDisplayName = (ticket) =>
  ticket?.requesterFullName ||
  ticket?.requesterName ||
  ticket?.requester?.fullName ||
  ticket?.createdByName ||
  ticket?.requesterEmail ||
  'N/A';

/** Split formatted datetime onto two lines so columns don't overflow into Action. */
const renderDateTimeCell = (value, emptyLabel = '') => {
  const formatted = formatDateTime(value);
  if (!formatted || formatted === '-') return emptyLabel;
  const spaceIdx = formatted.indexOf(' ');
  if (spaceIdx < 0) {
    return <span className="leading-snug break-words">{formatted}</span>;
  }
  return (
    <div className="leading-snug break-words">
      <div>{formatted.slice(0, spaceIdx)}</div>
      <div className="text-muted-foreground">{formatted.slice(spaceIdx + 1)}</div>
    </div>
  );
};

const SlaBreachedReport = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fromDate, setFromDate] = useState(() => getCurrentMonthDateRange().fromDate);
  const [toDate, setToDate] = useState(() => getCurrentMonthDateRange().toDate);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);
  const [paginationData, setPaginationData] = useState(null);
  const [hasGeneratedReport, setHasGeneratedReport] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [chartStats, setChartStats] = useState({ totalEscalated: 0, byStatus: [], byCategory: [] });
  const [chartDataLoading, setChartDataLoading] = useState(false);
  const listFetchIdRef = useRef(0);
  const statsFetchIdRef = useRef(0);
  const skipNextPageFetchRef = useRef(false);
  const initialLoadDoneRef = useRef(false);
  
  // Inquiry filter states
  const [selectedInquiryType, setSelectedInquiryType] = useState('all');
  const [selectedInquiryCategory, setSelectedInquiryCategory] = useState('all');
  const [selectedInquirySubCategory, setSelectedInquirySubCategory] = useState('all');
  const [selectedInquiryPriority, setSelectedInquiryPriority] = useState('all');
  const [selectedEscalationLevel, setSelectedEscalationLevel] = useState('all');
  const [selectedResponsibleRole, setSelectedResponsibleRole] = useState('all');
  const [selectedAgent, setSelectedAgent] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [escalatedFilter, setEscalatedFilter] = useState('all');
  const [slaBreachFilter, setSlaBreachFilter] = useState('all');
  const [selectedTag, setSelectedTag] = useState('all');
  const [ticketTags, setTicketTags] = useState([]);
  const [loadingTags, setLoadingTags] = useState(false);
  
  // Inquiry master data
  const [inquiryTypes, setInquiryTypes] = useState([]);
  const [inquiryCategories, setInquiryCategories] = useState([]);
  const [inquirySubCategories, setInquirySubCategories] = useState([]);
  const [inquiryPriorityLevels, setInquiryPriorityLevels] = useState([]);
  const [escalationLevels, setEscalationLevels] = useState([]);
  const [responsibleRoles, setResponsibleRoles] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loadingInquiryMasters, setLoadingInquiryMasters] = useState(false);
  const [loadingAgents, setLoadingAgents] = useState(false);
  
  const { data: statuses = [], isLoading: loadingStatuses } = useStatuses();

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchTerm(searchTerm.trim()), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);
  
  useEffect(() => {
    loadInquiryMasters();
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoadingTags(true);
    ticketService.getTicketTags()
      .then((resp) => {
        const raw = resp?.data?.data ?? resp?.data ?? resp;
        const rows = Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []);
        if (!cancelled) setTicketTags(rows);
      })
      .catch(() => {
        if (!cancelled) setTicketTags([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingTags(false);
      });
    return () => { cancelled = true; };
  }, []);
  
  const loadInquiryMasters = async () => {
    setLoadingInquiryMasters(true);
    try {
      const [typesResp, categoriesResp, subCategoriesResp, priorityLevelsResp, escalationLevelsResp, rolesResp] = await Promise.all([
        inquiryTypeMasterService.getAll(true),
        inquiryCategoryMasterService.getAll(true),
        inquirySubCategoryMasterService.getAll(true),
        ticketService.getInquiryPriorityLevelsForCreate(),
        inquiryEscalationLevelMasterService.getAll(true),
        registrationService.getRoles()
      ]);
      
      setInquiryTypes(masterRows(typesResp));
      setInquiryCategories(masterRows(categoriesResp));
      setInquirySubCategories(masterRows(subCategoriesResp));
      setInquiryPriorityLevels(masterRows(priorityLevelsResp));
      setEscalationLevels(
        [...masterRows(escalationLevelsResp)].sort((a, b) => (a.sequenceNumber ?? 0) - (b.sequenceNumber ?? 0))
      );
      setResponsibleRoles(masterRows(rolesResp).filter(isTicketHolderRole));
    } catch (error) {
      console.warn('Failed to load inquiry masters:', error);
    } finally {
      setLoadingInquiryMasters(false);
    }
  };

  /** Categories belong to a type, so narrow them once a type is picked (no extra API call). */
  const visibleInquiryCategories = useMemo(() => {
    if (selectedInquiryType === 'all') return inquiryCategories;
    return inquiryCategories.filter((c) => optionValue(c.inquiryTypeId) === selectedInquiryType);
  }, [inquiryCategories, selectedInquiryType]);

  const handleInquiryCategoryChange = async (value) => {
    setSelectedInquiryCategory(value);
    setSelectedInquirySubCategory('all');
    try {
      const rows = value === 'all'
        ? await inquirySubCategoryMasterService.getAll(true)
        : await inquirySubCategoryMasterService.getAll(true, Number(value));
      setInquirySubCategories(masterRows(rows));
    } catch (e) {
      console.warn('Failed to load inquiry sub categories:', e);
      setInquirySubCategories([]);
    }
  };

  const handleInquiryTypeChange = (value) => {
    setSelectedInquiryType(value);
    handleInquiryCategoryChange('all');
  };

  // Assignee options are fetched only for the chosen Responsible Role — no role selected, no users call.
  useEffect(() => {
    if (selectedResponsibleRole === 'all') {
      setAgents([]);
      return undefined;
    }
    let cancelled = false;
    setLoadingAgents(true);
    (async () => {
      try {
        const users = await getAssignableUsers(selectedResponsibleRole);
        if (!cancelled) setAgents(users);
      } catch (e) {
        if (!cancelled) {
          console.warn('Failed to load assignable users:', e);
          setAgents([]);
        }
      } finally {
        if (!cancelled) setLoadingAgents(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedResponsibleRole]);

  /** Only rendered with a role selected, so it explains an empty list rather than a missing role. */
  const assigneeHint = loadingAgents
    ? 'Loading users…'
    : agents.length === 0 ? 'No active users in this role' : null;

  // Keep Assignee valid when the Responsible Role narrows the user list.
  useEffect(() => {
    if (selectedAgent === 'all') return;
    if (!agents.some((u) => optionValue(u.userId ?? u.id) === selectedAgent)) setSelectedAgent('all');
  }, [agents, selectedAgent]);

  const resetFilters = () => {
    setSelectedInquiryType('all');
    setSelectedInquiryPriority('all');
    setSelectedEscalationLevel('all');
    setSelectedResponsibleRole('all');
    setSelectedAgent('all');
    setSelectedStatus('all');
    setEscalatedFilter('all');
    setSlaBreachFilter('all');
    setSelectedTag('all');
    handleInquiryCategoryChange('all');
  };

  const extractPagePayload = useCallback((response) => {
    const root = response?.data?.data ?? response?.data ?? response;
    if (root && Array.isArray(root.content)) return root;
    if (Array.isArray(root)) {
      return {
        content: root,
        totalElements: root.length,
        totalPages: 1,
        pageNumber: 0,
        pageSize: root.length || pageSize,
        first: true,
        last: true
      };
    }
    return null;
  }, [pageSize]);

  /** Field names match SlaBreachedReportRequest; 'all' selections are omitted, never sent as 0/"All". */
  const buildInquiryFilters = useCallback(() => {
    const statusRow = statuses.find(
      (s) => (s.statusCode || s.code || String(s.statusId || s.id)) === selectedStatus
    );
    return compactFilters({
      inquiryTypeId: optionalId(selectedInquiryType),
      inquiryCategoryId: optionalId(selectedInquiryCategory),
      inquirySubCategoryId: optionalId(selectedInquirySubCategory),
      inquiryPriorityLevelId: optionalId(selectedInquiryPriority),
      inquiryEscalationLevelId: optionalId(selectedEscalationLevel),
      responsibleRoleId: optionalId(selectedResponsibleRole),
      assigneeId: optionalId(selectedAgent),
      // Backend matches on status_code, so send the code and never the display name.
      status: selectedStatus === 'all'
        ? undefined
        : (statusRow?.statusCode || statusRow?.code || selectedStatus),
      escalated: optionalBool(escalatedFilter),
      slaBreach: optionalBool(slaBreachFilter),
      tagId: optionalId(selectedTag),
    });
  }, [selectedInquiryType, selectedInquiryCategory, selectedInquirySubCategory, selectedInquiryPriority, selectedEscalationLevel, selectedResponsibleRole, selectedAgent, selectedStatus, escalatedFilter, slaBreachFilter, selectedTag, statuses]);
  
  const loadStatistics = useCallback(async (rangeFrom = fromDate, rangeTo = toDate) => {
    if (!rangeFrom || !rangeTo) return;
    const fetchId = ++statsFetchIdRef.current;
    try {
      setChartDataLoading(true);
      const response = await apiRequest('/gateway/api/ticket/reports/sla-breached/statistics', {
        method: 'POST',
        body: { fromDate: rangeFrom, toDate: rangeTo, ...buildInquiryFilters() }
      });
      if (fetchId !== statsFetchIdRef.current) return;
      const data = response?.data?.data ?? response?.data ?? {};
      setChartStats({
        totalEscalated: Number(data.totalEscalated) || 0,
        byStatus: Array.isArray(data.byStatus) ? data.byStatus : [],
        byCategory: Array.isArray(data.byCategory) ? data.byCategory : []
      });
    } catch (err) {
      if (fetchId !== statsFetchIdRef.current) return;
      console.warn('SLA breached statistics load failed:', err);
      setChartStats({ totalEscalated: 0, byStatus: [], byCategory: [] });
    } finally {
      if (fetchId === statsFetchIdRef.current) {
        setChartDataLoading(false);
      }
    }
  }, [fromDate, toDate, buildInquiryFilters]);

  const loadList = useCallback(async (options = {}) => {
    const rangeFrom = options.fromDate ?? fromDate;
    const rangeTo = options.toDate ?? toDate;
    const page = options.page ?? currentPage;
    const search = options.search !== undefined ? options.search : debouncedSearchTerm;

    if (!rangeFrom || !rangeTo) return;
    if (new Date(rangeFrom) > new Date(rangeTo)) {
      toast.error('From Date must be before or equal to To Date');
      return;
    }

    const fetchId = ++listFetchIdRef.current;
    try {
      setLoading(true);
      setError('');
      const body = {
        fromDate: rangeFrom,
        toDate: rangeTo,
        page,
        size: pageSize,
        ...buildInquiryFilters()
      };
      if (search) body.search = search;

      const response = await apiRequest('/gateway/api/ticket/reports/sla-breached', {
        method: 'POST',
        body
      });
      if (fetchId !== listFetchIdRef.current) return;

      const pagePayload = extractPagePayload(response);
      const content = Array.isArray(pagePayload?.content) ? pagePayload.content : [];
      setTickets(content);
      setPaginationData({
        totalElements: pagePayload?.totalElements ?? content.length,
        totalPages: pagePayload?.totalPages ?? Math.max(1, Math.ceil((pagePayload?.totalElements ?? content.length) / pageSize) || 1),
        pageNumber: pagePayload?.pageNumber ?? page,
        pageSize: pagePayload?.pageSize ?? pageSize,
        first: pagePayload?.first ?? page === 0,
        last: pagePayload?.last ?? true
      });
    } catch (err) {
      if (fetchId !== listFetchIdRef.current) return;
      console.error('Error loading SLA breached list:', err);
      setError(extractErrorMessage(err, 'Failed to load report'));
      toast.error(extractErrorMessage(err, 'Failed to load report'));
      setTickets([]);
      setPaginationData(null);
    } finally {
      if (fetchId === listFetchIdRef.current) {
        setLoading(false);
      }
    }
  }, [fromDate, toDate, currentPage, pageSize, debouncedSearchTerm, extractPagePayload, buildInquiryFilters]);

  const refreshBoth = useCallback(async (options = {}) => {
    const resetToFirstPage = options.resetToFirstPage !== false;
    const rangeFrom = options.fromDate ?? fromDate;
    const rangeTo = options.toDate ?? toDate;
    if (!rangeFrom || !rangeTo) return;
    if (new Date(rangeFrom) > new Date(rangeTo)) {
      toast.error('From Date must be before or equal to To Date');
      return;
    }

    const pageToUse = resetToFirstPage ? 0 : currentPage;
    if (resetToFirstPage && currentPage !== 0) {
      skipNextPageFetchRef.current = true;
      setCurrentPage(0);
    }

    await Promise.all([
      loadStatistics(rangeFrom, rangeTo),
      loadList({
        fromDate: rangeFrom,
        toDate: rangeTo,
        page: pageToUse,
        search: options.search !== undefined ? options.search : debouncedSearchTerm
      })
    ]);
  }, [fromDate, toDate, currentPage, debouncedSearchTerm, loadStatistics, loadList]);

  // Initial load: statistics + list page 0
  useEffect(() => {
    if (!hasGeneratedReport || !fromDate || !toDate) return;
    (async () => {
      await refreshBoth({ resetToFirstPage: true, fromDate, toDate, search: '' });
      initialLoadDoneRef.current = true;
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pagination / page size → list only
  useEffect(() => {
    if (!hasGeneratedReport || !initialLoadDoneRef.current) return;
    if (skipNextPageFetchRef.current) {
      skipNextPageFetchRef.current = false;
      return;
    }
    loadList({ page: currentPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize]);

  // Search → list only (reset to page 0)
  useEffect(() => {
    if (!hasGeneratedReport || !initialLoadDoneRef.current) return;
    if (currentPage !== 0) {
      skipNextPageFetchRef.current = true;
      setCurrentPage(0);
    }
    loadList({ page: 0, search: debouncedSearchTerm });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  const generateReport = async () => {
    if (!fromDate || !toDate) {
      toast.error('Please select both From Date and To Date');
      return;
    }
    if (new Date(fromDate) > new Date(toDate)) {
      toast.error('From Date must be before or equal to To Date');
      return;
    }
    setHasGeneratedReport(true);
    await refreshBoth({ resetToFirstPage: true });
    toast.success('Report generated');
  };

  const fetchAllSlaBreachedForExport = async () => {
    const baseBody = { fromDate, toDate, ...buildInquiryFilters(), ...(debouncedSearchTerm ? { search: debouncedSearchTerm } : {}) };
    const first = await apiRequest('/gateway/api/ticket/reports/sla-breached', {
      method: 'POST',
      body: { ...baseBody, page: 0, size: 10 }
    });
    const firstPage = extractPagePayload(first);
    const total = Number(firstPage?.totalElements) || 0;
    if (total <= 0) return [];
    if (total <= 10 && Array.isArray(firstPage?.content)) return firstPage.content;
    const full = await apiRequest('/gateway/api/ticket/reports/sla-breached', {
      method: 'POST',
      body: { ...baseBody, page: 0, size: total }
    });
    const fullPage = extractPagePayload(full);
    return Array.isArray(fullPage?.content) ? fullPage.content : [];
  };

  const pieChartData = useMemo(() => {
    return (chartStats.byStatus || [])
      .map((item) => ({
        name: String(item.name || 'Unknown').trim() || 'Unknown',
        value: Number(item.count) || 0
      }))
      .filter((d) => d.value > 0);
  }, [chartStats]);

  const handleViewTicket = (ticket) => {
    const ticketId = ticket?.ticketId || ticket?.id;
    if (!ticketId) {
      toast.error('Ticket ID not available');
      return;
    }
    navigate(`/admin/tickets/${ticketId}`, { state: { mode: 'view' } });
  };

  const barChartData = useMemo(() => {
    return (chartStats.byCategory || [])
      .map((item) => {
        const fullName = String(item.name || 'Uncategorized').trim() || 'Uncategorized';
        return {
          name: fullName.substring(0, 20),
          count: Number(item.count) || 0,
          fullName
        };
      })
      .filter((d) => d.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [chartStats]);

  const CHART_COLORS = ['#EF4444', '#F59E0B', '#3B82F6', '#10B981', '#8B5CF6', '#EC4899'];
  const hasChartData = pieChartData.length > 0 || barChartData.length > 0;

  const escapeCsvCell = (val) => {
    if (val == null || val === '') return '';
    const s = String(val);
    if (/[,"\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const handleExportCsv = async () => {
    setExportMenuOpen(false);
    if (!fromDate || !toDate) {
      toast.error('Please select date range and generate report first');
      return;
    }
    setExporting(true);
    try {
      const allTickets = await fetchAllSlaBreachedForExport();
      if (allTickets.length === 0) {
        toast.error('No data to export');
        return;
      }
      const headers = ['S.NO', 'Ticket #', 'Subject', 'Requester', 'Inquiry Type', 'Category', 'Sub Category', 'Priority', 'Status', 'Tags', 'Breached Level', 'Escalated To', 'Responsible Role', 'Assignee At Breach', 'New Assignee', 'Breach Time', 'Current SLA', 'Ticket Escalated', 'SLA Breach Flag'];
      const rows = allTickets.map((t, i) => [
        i + 1,
        t.ticketNumber || 'N/A',
        t.subject || 'N/A',
        t.requesterEmail || getRequesterDisplayName(t) || 'N/A',
        t.inquiryTypeName || '—',
        resolveTicketCategoryLabel(t),
        t.inquirySubCategoryName || '—',
        resolveTicketPriorityLabel(t),
        t.status || 'N/A',
        formatTicketTagsLabel(t, ''),
        t.breachedEscalationLevelName || t.escalationLevelName || '—',
        t.escalatedToLevelName || '—',
        t.breachedResponsibleRoleName || t.responsibleRoleName || '—',
        t.assigneeAtBreachName || t.assigneeFullName || 'Unassigned',
        t.assigneeAfterEscalationName || '—',
        formatDateTime(t.breachEventAt || t.escalatedTime),
        resolveCurrentSlaWindow(t, 0, statuses)?.label || '—',
        t.escalated === true ? 'Yes' : 'No',
        (t.slaBreach || t.slaIsBreached) ? 'Yes' : '—'
      ].map(escapeCsvCell));
      const csv = [headers.map(escapeCsvCell).join(','), ...rows.map((r) => r.join(','))].join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sla-breached-report-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Report exported as CSV');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    setExportMenuOpen(false);
    if (!fromDate || !toDate) {
      toast.error('Please select date range and generate report first');
      return;
    }
    setExporting(true);
    try {
      const allTickets = await fetchAllSlaBreachedForExport();
      if (allTickets.length === 0) {
        toast.error('No data to export');
        return;
      }
      const doc = new jsPDF({ orientation: 'landscape' });
      doc.setFontSize(14);
      doc.text('SLA Breached Report', 14, 15);
      doc.setFontSize(10);
      const tableData = allTickets.map((t, i) => [
        i + 1,
        t.ticketNumber || 'N/A',
        (t.subject || 'N/A').substring(0, 30),
        (t.requesterEmail || getRequesterDisplayName(t) || 'N/A').substring(0, 20),
        (t.inquiryTypeName || '—').substring(0, 12),
        resolveTicketCategoryLabel(t).substring(0, 12),
        (t.inquirySubCategoryName || '—').substring(0, 12),
        resolveTicketPriorityLabel(t).substring(0, 10),
        (t.status || 'N/A').substring(0, 10),
        formatTicketTagsLabel(t, '').substring(0, 16),
        (t.breachedEscalationLevelName || t.escalationLevelName || '—').substring(0, 10),
        (t.escalatedToLevelName || '—').substring(0, 10),
        (t.breachedResponsibleRoleName || t.responsibleRoleName || '—').substring(0, 10),
        (t.assigneeAtBreachName || t.assigneeFullName || 'Unassigned').substring(0, 12),
        (t.assigneeAfterEscalationName || '—').substring(0, 12),
        formatDateTime(t.breachEventAt || t.escalatedTime),
        (resolveCurrentSlaWindow(t, 0, statuses)?.label || '—').substring(0, 15),
        t.escalated === true ? 'Yes' : 'No',
        (t.slaBreach || t.slaIsBreached) ? 'Yes' : '—'
      ]);
      autoTable(doc, {
        startY: 22,
        head: [['#', 'ID', 'Subject', 'Req', 'Type', 'Cat', 'SubCat', 'Pri', 'Status', 'Tags', 'Br.Lvl', 'Esc.To', 'Role', 'AtBr', 'NewAsg', 'Breach', 'SLA', 'Esc', 'Flag']],
        body: tableData,
        styles: { fontSize: 5 }
      });
      doc.save(`sla-breached-report-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success('Report exported as PDF');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (paginationData && newPage >= 0 && newPage < paginationData.totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">SLA Breached Reports</h1>
          <p className="text-sm text-muted-foreground mt-1">
            One row per SLA breach event in the selected date range (historical escalation levels preserved)
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setShowFilters((prev) => !prev);
              setExportMenuOpen(false);
            }}
            className="flex items-center gap-2"
            aria-expanded={showFilters}
          >
            <Filter className="h-4 w-4" />
            Filter
            {hasGeneratedReport && !showFilters && (
              <span className="ml-1 h-2 w-2 rounded-full bg-primary" aria-hidden />
            )}
          </Button>
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setExportMenuOpen((v) => !v)}
              disabled={loading || exporting || !fromDate || !toDate || !paginationData?.totalElements}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              {exporting ? 'Exporting…' : 'Export'}
              <ChevronDown className="h-4 w-4 ml-1" />
            </Button>
            {exportMenuOpen && (
              <div className="absolute right-0 top-full mt-1 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-10 min-w-[140px]">
                <button type="button" className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2" onClick={handleExportCsv}>
                  <FileText className="h-4 w-4" /> Export CSV
                </button>
                <button type="button" className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2" onClick={handleExportPdf}>
                  <FileText className="h-4 w-4" /> Export PDF
                </button>
              </div>
            )}
          </div>
          <Button
            onClick={() => {
              refreshBoth({ resetToFirstPage: false });
            }}
            disabled={loading || chartDataLoading || !hasGeneratedReport || !fromDate || !toDate}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Date Range Filter Card (toggle via Filter button) */}
      {showFilters && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
           Date Range Filter
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                From Date
              </label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                To Date
              </label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-full"
              />
            </div>

            {/* Inquiry Type Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Inquiry Type
              </label>
              <Select
                value={selectedInquiryType}
                onValueChange={handleInquiryTypeChange}
                disabled={loadingInquiryMasters}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {inquiryTypes.map((type) => (
                    <SelectItem key={type.id} value={optionValue(type.id)}>
                      {type.inquiryTypeName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Inquiry Category Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Inquiry Category
              </label>
              <Select
                value={selectedInquiryCategory}
                onValueChange={handleInquiryCategoryChange}
                disabled={loadingInquiryMasters}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {visibleInquiryCategories.map((cat) => (
                    <SelectItem key={cat.id} value={optionValue(cat.id)}>
                      {cat.inquiryCategoryName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Inquiry Sub Category Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Inquiry Sub Category
              </label>
              <Select
                value={selectedInquirySubCategory}
                onValueChange={setSelectedInquirySubCategory}
                disabled={loadingInquiryMasters}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {inquirySubCategories.map((subCat) => (
                    <SelectItem key={subCat.id} value={optionValue(subCat.id)}>
                      {subCat.inquirySubCategoryName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Inquiry Priority Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Inquiry Priority
              </label>
              <Select
                value={selectedInquiryPriority}
                onValueChange={setSelectedInquiryPriority}
                disabled={loadingInquiryMasters}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {inquiryPriorityLevels.map((pri) => (
                    <SelectItem key={pri.id} value={optionValue(pri.id)}>
                      {pri.priorityLevelName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Breached Level Filter — level whose SLA expired at the event */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Breached Level
              </label>
              <Select
                value={selectedEscalationLevel}
                onValueChange={setSelectedEscalationLevel}
                disabled={loadingInquiryMasters}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {escalationLevels.map((level) => (
                    <SelectItem key={level.id} value={optionValue(level.id)}>
                      {level.escalationLevelName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Responsible Role Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Responsible Role
              </label>
              <Select
                value={selectedResponsibleRole}
                onValueChange={setSelectedResponsibleRole}
                disabled={loadingInquiryMasters}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {responsibleRoles.map((role) => (
                    <SelectItem key={role.roleId} value={optionValue(role.roleId)}>
                      {role.label || role.roleCode}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Agent Filter — only meaningful once a Responsible Role scopes the user list */}
            {selectedResponsibleRole !== 'all' && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Assignee
                </label>
                <Select
                  value={selectedAgent}
                  onValueChange={setSelectedAgent}
                  disabled={loadingAgents || agents.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {agents.map((agent) => (
                      <SelectItem
                        key={agent.userId ?? agent.id}
                        value={optionValue(agent.userId ?? agent.id)}
                      >
                        {getUserDisplayName(agent)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {assigneeHint && (
                  <p className="mt-1 text-xs text-muted-foreground">{assigneeHint}</p>
                )}
              </div>
            )}

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Status
              </label>
              <Select
                value={selectedStatus}
                onValueChange={setSelectedStatus}
                disabled={loadingStatuses}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {statuses.map((status) => {
                    const statusValue = status.statusCode || status.code || String(status.statusId || status.id);
                    const statusDisplay = status.statusName || status.name || 'Unknown';
                    return (
                      <SelectItem
                        key={status.statusId || status.id}
                        value={statusValue}
                      >
                        {statusDisplay}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Escalated Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Escalated
              </label>
              <Select
                value={escalatedFilter}
                onValueChange={setEscalatedFilter}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* SLA Breach Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                SLA Breach
              </label>
              <Select
                value={slaBreachFilter}
                onValueChange={setSlaBreachFilter}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Tag Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Tag
              </label>
              <Select
                value={selectedTag}
                onValueChange={setSelectedTag}
                disabled={loadingTags}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Tags" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tags</SelectItem>
                  {ticketTags.map((tag) => (
                    <SelectItem
                      key={tag.tagId ?? tag.id}
                      value={String(tag.tagId ?? tag.id)}
                    >
                      {tag.tagName || tag.name || 'Tag'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end">
              <Button
                onClick={resetFilters}
                disabled={loading}
                variant="outline"
                className="w-full"
              >
                <X className="h-4 w-4 mr-2" />
                Reset Filters
              </Button>
            </div>

            <div className="flex items-end">
              <Button
                onClick={generateReport}
                disabled={loading || !fromDate || !toDate}
                className="w-full"
              >
                {loading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4 mr-2" />
                    Generate Report
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-800 dark:text-red-200">
            <AlertTriangle className="h-5 w-5" />
            <span className="font-medium">{error}</span>
          </div>
        </div>
      )}

      {/* Charts: Pie (by status) and Bar (by category) — from statistics API */}
      {!chartDataLoading && hasChartData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <TicketIcon className="h-5 w-5" />
                SLA Breached by status
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Distribution of all tickets in the selected date range
              </p>
            </CardHeader>
            <CardContent>
              {pieChartData.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">No data to display</p>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={pieChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {pieChartData.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name) => [value, name]}
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <AlertTriangle className="h-5 w-5" />
                SLA Breached by category
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                By category (Inquiry Category when present)
              </p>
            </CardHeader>
            <CardContent>
              {barChartData.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">No data to display</p>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart
                    data={barChartData}
                    margin={{ top: 8, right: 8, left: 8, bottom: 24 }}
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis type="number" allowDecimals={false} className="text-xs" />
                    <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
                    <Tooltip
                      formatter={(value) => [value, 'Tickets']}
                      labelFormatter={(_, payload) => payload?.[0]?.payload?.fullName ?? ''}
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar dataKey="count" fill={CHART_COLORS[0]} name="Tickets" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Results Table */}
      {(tickets.length > 0 || loading || (paginationData && paginationData.totalElements > 0) || debouncedSearchTerm) && hasGeneratedReport && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <TicketIcon className="h-5 w-5" />
                SLA Breach Events ({paginationData ? paginationData.totalElements : tickets.length})
              </CardTitle>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search tickets..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-64"
                  />
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full table-fixed border-collapse min-w-[1800px]">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-3 text-sm font-semibold text-foreground w-[3%]">S.NO</th>
                    <th className="text-left p-3 text-sm font-semibold text-foreground w-[7%]">Ticket #</th>
                    <th className="text-left p-3 text-sm font-semibold text-foreground w-[8%]">Requester</th>
                    <th className="text-left p-3 text-sm font-semibold text-foreground w-[10%]">Subject</th>
                    <th className="text-left p-3 text-sm font-semibold text-foreground w-[6%]">Category</th>
                    <th className="text-left p-3 text-sm font-semibold text-foreground w-[5%]">Priority</th>
                    <th className="text-left p-3 text-sm font-semibold text-foreground w-[5%]">Status</th>
                    <th className="text-left p-3 text-sm font-semibold text-foreground w-[6%]">Tags</th>
                    <th className="text-left p-3 text-sm font-semibold text-foreground w-[6%]">Breached Lvl</th>
                    <th className="text-left p-3 text-sm font-semibold text-foreground w-[6%]">Escalated To</th>
                    <th className="text-left p-3 text-sm font-semibold text-foreground w-[6%]">Role</th>
                    <th className="text-left p-3 text-sm font-semibold text-foreground w-[6%]">At Breach</th>
                    <th className="text-left p-3 text-sm font-semibold text-foreground w-[6%]">New Assignee</th>
                    <th className="text-left p-3 text-sm font-semibold text-foreground w-[8%]">Breach Time</th>
                    <th className="text-left p-3 text-sm font-semibold text-foreground w-[6%]">Current SLA</th>
                    <th className="text-left p-3 text-sm font-semibold text-foreground w-[5%]">Breach</th>
                    <th className="text-left p-3 text-sm font-semibold text-foreground w-[6%]">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.length === 0 ? (
                    <tr>
                      <td colSpan="17" className="text-center p-8 text-muted-foreground">
                        {loading ? 'Loading tickets...' : (searchTerm ? 'No tickets found matching your search criteria' : 'No tickets found')}
                      </td>
                    </tr>
                  ) : (
                    tickets.map((ticket, index) => {
                      const serialNumber = (currentPage * pageSize) + index + 1;
                      const requesterName = getRequesterDisplayName(ticket);
                      return (
                      <tr key={ticket.escalationEventId || `${ticket.ticketId}-${index}`} className="border-b border-border hover:bg-muted/50">
                        <td className="p-3 text-sm text-foreground align-top">
                          {serialNumber}
                        </td>
                        <td className="p-3 text-sm align-top whitespace-nowrap">
                          <span className="font-medium text-foreground">
                            {ticket.ticketNumber || 'N/A'}
                          </span>
                        </td>
                        <td className="p-3 text-sm text-foreground align-top">
                          <div className="flex items-center gap-2 min-w-0">
                            <User className="h-4 w-4 text-muted-foreground shrink-0" />
                            <span className="truncate" title={requesterName}>
                              {requesterName}
                            </span>
                          </div>
                        </td>
                        <td className="p-3 text-sm text-foreground align-top">
                          <p
                            className="break-all line-clamp-3 leading-snug"
                            title={ticket.subject || 'N/A'}
                          >
                            {ticket.subject || 'N/A'}
                          </p>
                        </td>
                        <td className="p-3 text-sm text-foreground align-top">
                          <span className="truncate block" title={resolveTicketCategoryLabel(ticket)}>
                            {resolveTicketCategoryLabel(ticket)}
                          </span>
                        </td>
                        <td className="p-3 text-sm text-foreground align-top whitespace-nowrap">
                          <span className="truncate block" title={resolveTicketPriorityLabel(ticket)}>
                            {resolveTicketPriorityLabel(ticket)}
                          </span>
                        </td>
                        <td className="p-3 text-sm align-top whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                            ticket.status === 'escalated' 
                              ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-200'
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                          }`}>
                            {ticket.status || 'N/A'}
                          </span>
                        </td>
                        <td className="p-3 text-sm text-foreground align-top">
                          <span className="line-clamp-2 leading-snug break-words" title={formatTicketTagsLabel(ticket)}>
                            {formatTicketTagsLabel(ticket)}
                          </span>
                        </td>
                        <td className="p-3 text-sm text-foreground align-top">
                          <span className="truncate block" title={ticket.breachedEscalationLevelName || ticket.escalationLevelName || '—'}>
                            {ticket.breachedEscalationLevelName || ticket.escalationLevelName || '—'}
                          </span>
                        </td>
                        <td className="p-3 text-sm text-foreground align-top">
                          <span className="truncate block" title={ticket.escalatedToLevelName || '—'}>
                            {ticket.escalatedToLevelName || '—'}
                          </span>
                        </td>
                        <td className="p-3 text-sm text-foreground align-top">
                          <span className="truncate block" title={ticket.breachedResponsibleRoleName || ticket.responsibleRoleName || '—'}>
                            {ticket.breachedResponsibleRoleName || ticket.responsibleRoleName || '—'}
                          </span>
                        </td>
                        <td className="p-3 text-sm text-foreground align-top">
                          <span className="truncate block" title={ticket.assigneeAtBreachName || ticket.assigneeFullName || 'Unassigned'}>
                            {ticket.assigneeAtBreachName || ticket.assigneeFullName || 'Unassigned'}
                          </span>
                        </td>
                        <td className="p-3 text-sm text-foreground align-top">
                          <span className="truncate block" title={ticket.assigneeAfterEscalationName || '—'}>
                            {ticket.assigneeAfterEscalationName || '—'}
                          </span>
                        </td>
                        <td className="p-3 text-sm text-foreground align-top">
                          <div className="flex items-start gap-2">
                            <Clock className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                            {renderDateTimeCell(ticket.breachEventAt || ticket.escalatedTime)}
                          </div>
                        </td>
                        <td className="p-3 text-sm text-foreground align-top whitespace-nowrap">
                          <span className="truncate block" title={resolveCurrentSlaWindow(ticket, 0, statuses)?.label || '—'}>
                            {resolveCurrentSlaWindow(ticket, 0, statuses)?.label || '—'}
                          </span>
                        </td>
                        <td className="p-3 text-sm align-top whitespace-nowrap">
                          {ticket.slaBreach || ticket.slaIsBreached ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-200">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              Yes
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="p-3 text-sm align-top whitespace-nowrap">
                          <Button
                            size="sm"
                            variant="outline"
                            className="inline-flex items-center gap-1.5 shrink-0"
                            onClick={() => handleViewTicket(ticket)}
                          >
                            <Eye className="h-4 w-4 shrink-0" />
                            View
                          </Button>
                        </td>
                      </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            {paginationData && paginationData.totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  Showing <span className="font-medium">{(paginationData.pageNumber * paginationData.pageSize) + 1}</span> to{' '}
                  <span className="font-medium">{Math.min((paginationData.pageNumber + 1) * paginationData.pageSize, paginationData.totalElements)}</span> of{' '}
                  <span className="font-medium">{paginationData.totalElements}</span> tickets
                  {' '}(Page {paginationData.pageNumber + 1} of {paginationData.totalPages})
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={paginationData.first || loading}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <div className="flex items-center space-x-1">
                    {Array.from({ length: Math.min(paginationData.totalPages, 5) }, (_, i) => {
                      let pageNum;
                      if (paginationData.totalPages <= 5) {
                        pageNum = i;
                      } else if (currentPage < 3) {
                        pageNum = i;
                      } else if (currentPage > paginationData.totalPages - 4) {
                        pageNum = paginationData.totalPages - 5 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <Button
                          key={pageNum}
                          variant={currentPage === pageNum ? "default" : "outline"}
                          size="sm"
                          onClick={() => handlePageChange(pageNum)}
                          disabled={loading}
                          className="min-w-[2.5rem]"
                        >
                          {pageNum + 1}
                        </Button>
                      );
                    })}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={paginationData.last || loading}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!loading && !chartDataLoading && tickets.length === 0 && !hasChartData && fromDate && toDate && (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">No Escalated Tickets Found</h3>
            <p className="text-sm text-muted-foreground">
              No tickets were escalated within the selected date range.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SlaBreachedReport;

